import { Address, ApplicationAddressUpdate, isNewAddress } from '@bybeam/platform-types';
import { GenericFormData, fieldUpdate } from '@platform-ui-common/hooks/useForm/index.js';
import useGeocoder from '@platform-ui-common/hooks/useGeocoder.js';
import { useCallback, useState } from 'react';

export function getAddressKey(subKey: string, key?: string): string {
  return [key, subKey].filter(Boolean).join('.');
}

export function getUnhousedKey(key?: string): string {
  return getAddressKey('isUnhoused', key);
}

export function formatAddressFields(
  addressParts: string[],
  formData: GenericFormData,
  key?: string,
): string {
  const label = addressParts.map((part) => formData[getAddressKey(part, key)]).filter(Boolean);
  return label.length ? label.join(', ') : '';
}

export function resetAddress(dispatch, key: string, address?: Address): void {
  dispatch(fieldUpdate(`${key}.city`, address?.city ?? ''));
  dispatch(fieldUpdate(`${key}.zip`, address?.zip ?? ''));
  dispatch(fieldUpdate(`${key}.state`, address?.state ?? ''));
  dispatch(fieldUpdate(`${key}.addressLine1`, address?.addressLine1 ?? ''));
  dispatch(fieldUpdate(`${key}.addressLine2`, address?.addressLine2 ?? ''));
}

// TODO: Could we do this address validation in the API instead of in the UI
// any time we need to submit an address?
export function useValidateAddresses(): {
  loading: boolean;
  validateAddresses: (
    addresses: ApplicationAddressUpdate[],
    options?: { manualOverride?: boolean },
  ) => Promise<ApplicationAddressUpdate[]>;
} {
  const { validateAddress } = useGeocoder();
  const [loading, setLoading] = useState(false);

  const validateAddresses = useCallback(
    async (
      addresses: ApplicationAddressUpdate[],
      options?: { manualOverride?: boolean },
    ): Promise<ApplicationAddressUpdate[]> => {
      setLoading(true);
      const validatedAddresses = await Promise.all(
        addresses.map(async (addressUpdate): Promise<ApplicationAddressUpdate> => {
          // If the address is optional and was not filled, is returned as null
          if (
            isNewAddress(addressUpdate) &&
            !Object.keys(addressUpdate.address).some((formKey) => {
              return addressUpdate.address[formKey] !== '';
            })
          )
            return { key: addressUpdate.key, address: null };

          if (!isNewAddress(addressUpdate)) return addressUpdate;

          const { key, address } = addressUpdate;
          const validatedAddress = await validateAddress(address);

          if (options?.manualOverride) {
            return { key, address: { ...address, county: validatedAddress.county } };
          }
          return { key, address: validatedAddress };
        }),
      );
      setLoading(false);
      return validatedAddresses;
    },
    [validateAddress],
  );
  return { loading, validateAddresses };
}
